import { LogoutOutlined, MenuOutlined, UserOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Drawer,
  Dropdown,
  Flex,
  Grid,
  Layout,
  Menu,
  Select,
  Space,
} from "antd";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import { useAuth } from "../../context/AuthContext";
import { useAppLanguage } from "../../i18n/useAppLanguage";
import projectStore from "../../stores/ProjectStore";
import { getMenuItems } from "./MenuConfig";
import { headerStyle, languageOption } from "./headerConstants";

const ProjectSelector = React.lazy(() => import("../ProjectSelector"));

const { Header: AntHeader } = Layout;

function Header({ selectedKeys }) {
  const { t } = useTranslation();
  const { user, logout, isAuthenticated, appConfig } = useAuth();
  const { language, setLanguage, supportedLanguages } = useAppLanguage();
  const { allowedServices, assignedProjects, assignedProjectsLoaded, allServices } = projectStore;

  const isAdmin = user?.role === "admin" || user?.role === "sysAdmin";

  useEffect(() => {
    if (isAdmin) {
      projectStore.loadAllServices();
    }
  }, [isAdmin]);

  const hasAssignedProjects =
    assignedProjectsLoaded && assignedProjects.length > 0;

  const menuItems = getMenuItems(
    user,
    appConfig,
    allowedServices,
    t,
    hasAssignedProjects,
    allServices,
  );

  const navigate = useNavigate();

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md; // < 768px: compact logo/padding/avatar + Drawer nav

  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!isMobile && drawerOpen) setDrawerOpen(false);
  }, [isMobile, drawerOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const userMenuItems = [
    { key: "profile", icon: <UserOutlined />, label: t("header.profile"), onClick: () => navigate("/profile", { replace: true }) },
    { type: "divider" },
    { key: "logout", icon: <LogoutOutlined />, label: t("header.logout"), onClick: handleLogout, danger: true },
  ];

  const authControls = isAuthenticated && user ? (
    <>
      {!isMobile && (
        <React.Suspense fallback={null}>
          <ProjectSelector />
        </React.Suspense>
      )}
      <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
        <Button type="text" style={{ padding: "4px 12px" }}>
          <Space>
            <Avatar size="small" icon={<UserOutlined />} />
            {!isMobile && <span>{user.username || user.email}</span>}
          </Space>
        </Button>
      </Dropdown>
    </>
  ) : (
    <Flex gap={isMobile ? 8 : 10} justify="center" align="center">
      <Button type="primary" size={isMobile ? "middle" : "large"} onClick={() => navigate("/login")}>
        {t("signup.actions.signUp")}
      </Button>
      <Button size={isMobile ? "middle" : "large"} onClick={() => navigate("/login")}>
        {t("header.login")}
      </Button>
    </Flex>
  );

  return (
    <AntHeader
      style={{ ...headerStyle, padding: isMobile ? "0 12px" : "0 16px" }}
    >
      <img
        src={logo}
        alt="Logo"
        style={{
          maxHeight: isMobile ? 40 : 70,
          paddingTop: isMobile ? 4 : 10,
          paddingBottom: isMobile ? 4 : 10,
          cursor: "pointer",
        }}
        onClick={() => navigate("/")}
      />

      {!isMobile && (
        <Menu
          mode="horizontal"
          selectedKeys={selectedKeys}
          items={menuItems}
          style={{ flex: 1, marginLeft: 16, minWidth: 0 }}
        />
      )}

      <Flex
        gap={isMobile ? 8 : 10}
        justify="flex-end"
        align="center"
        style={{
          marginLeft: isMobile ? "auto" : 16,
          flex: "0 0 auto",
        }}
      >
        {isMobile && user && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            aria-label={t("header.menu", "Menu")}
            onClick={() => setDrawerOpen(true)}
          />
        )}
        <Select
          value={language}
          onChange={setLanguage}
          size="large"
          aria-label={t("header.languageLabel")}
          options={supportedLanguages.map(languageOption)}
        />
        {authControls}
      </Flex>

      <Drawer
        placement="left"
        open={isMobile && drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={280}
        styles={{ body: { padding: 0 } }}
      >
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          items={menuItems}
          onSelect={() => setDrawerOpen(false)}
          style={{ borderInlineEnd: "none" }}
        />
        {isMobile && isAuthenticated && (
          <div style={{ padding: 16 }}>
            <React.Suspense fallback={null}>
              <ProjectSelector />
            </React.Suspense>
          </div>
        )}
      </Drawer>
    </AntHeader>
  );
}

export default observer(Header);
